import type { TransferListItem } from '@ibsys/kit';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { InfoIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { declOfNum } from '@/shared/lib/utils/string';

type Props = {
  added: TransferListItem[];
  removed: TransferListItem[];
  totalCount: number;
};

export const ColumnChangesPopover: FC<Props> = ({ added, removed, totalCount }) => {
  const { t } = useTranslation(['dialogs']);

  const changesText = useMemo(() => {
    const wordForm = declOfNum(totalCount, [
      t('dialogs:attributeSettingsChangesCount_one'),
      t('dialogs:attributeSettingsChangesCount_few'),
      t('dialogs:attributeSettingsChangesCount_many'),
    ]);
    return `${totalCount} ${wordForm}`;
  }, [totalCount, t]);

  return (
    <div className={'flex items-center gap-1'}>
      <span>{changesText}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-foreground"
            aria-label={changesText}
          >
            <InfoIcon className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start" side="top" portal={{ enable: false }}>
          <ColumnChangesPopoverContent added={added} removed={removed} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

type ContentProps = {
  added: TransferListItem[];
  removed: TransferListItem[];
};

const ColumnChangesPopoverContent: FC<ContentProps> = ({ added, removed }) => {
  const { t } = useTranslation(['dialogs']);

  const renderGroup = (items: TransferListItem[]) =>
    items.map((item) => (
      <div key={item.id} className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-muted-foreground">{item.label}</span>
        {item.children?.length && (
          <ul className="flex flex-col gap-0.5 pl-2 border-l-2 border-border/60">
            {item.children.map((item) => {
              return (
                <li key={String(item.id)} className="text-xs text-foreground leading-snug break-words">
                  {item.label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    ));

  return (
    <div className="grid grid-cols-2 gap-4 text-left min-w-0">
      {!!added.length && (
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
            {t('dialogs:attributeSettingsTooltipAdded')}
          </span>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto overscroll-contain min-h-0">
            {renderGroup(added)}
          </div>
        </div>
      )}
      {!!removed.length && (
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
            {t('dialogs:attributeSettingsTooltipRemoved')}
          </span>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto overscroll-contain min-h-0">
            {renderGroup(removed)}
          </div>
        </div>
      )}
    </div>
  );
};
